/**
 * Business-context mutation primitives.
 *
 * Server is authoritative; these wrap the upserts called by the wizard.
 * RLS uses `auth.uid()` so we never need to attach `user_id` to upserts —
 * but `user_business_context` has user_id as PK, so upsert still requires it.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";

export type UBCRow = Database["public"]["Tables"]["user_business_context"]["Row"];
export type UBCUpdate = Database["public"]["Tables"]["user_business_context"]["Update"];

export interface UpdateBusinessContextArgs {
  client: SupabaseClient<Database>;
  userId: string; // profiles.id (UUID)
  patch: UBCUpdate;
}

export interface ProfileIdentityPatch {
  display_name?: string;
  honorific_prefix?: "" | "Dr." | "Dra." | null;
}

export async function updateProfileIdentity(
  client: SupabaseClient<Database>,
  userId: string,
  patch: ProfileIdentityPatch,
): Promise<void> {
  const updatePatch: Database["public"]["Tables"]["profiles"]["Update"] = {};
  if (typeof patch.display_name === "string") {
    updatePatch.display_name = patch.display_name;
  }
  if (patch.honorific_prefix !== undefined) {
    updatePatch.honorific_prefix = patch.honorific_prefix;
  }
  if (Object.keys(updatePatch).length === 0) return;
  const { error } = await client
    .from("profiles")
    .update(updatePatch)
    .eq("id", userId);
  if (error) throw error;
}

export async function updateBusinessContext({
  client,
  userId,
  patch,
}: UpdateBusinessContextArgs): Promise<UBCRow> {
  const { data, error } = await client
    .from("user_business_context")
    .upsert({ user_id: userId, ...patch }, { onConflict: "user_id" })
    .select("*")
    .single();
  if (error) {
    console.error("[updateBusinessContext] upsert failed:", error, "patch keys:", Object.keys(patch));
    throw error;
  }
  return data as UBCRow;
}

const ALLOWED_AVATAR_MIME = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

export interface UploadAvatarArgs {
  client: SupabaseClient<Database>;
  file: File;
  userId: string; // profiles.id (UUID) — used as folder name
}

export interface UploadAvatarResult {
  publicUrl: string;
  path: string;
}

export async function uploadAvatar({
  client,
  file,
  userId,
}: UploadAvatarArgs): Promise<UploadAvatarResult> {
  if (!ALLOWED_AVATAR_MIME.includes(file.type as (typeof ALLOWED_AVATAR_MIME)[number])) {
    throw new Error("Formato inválido. Use JPG, PNG ou WebP.");
  }
  if (file.size > MAX_AVATAR_BYTES) {
    throw new Error("Imagem maior que 2MB.");
  }

  const ext = file.name.includes(".") ? file.name.split(".").pop() : "jpg";
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;

  const { error: uploadError } = await client.storage
    .from("avatars")
    .upload(path, file, { contentType: file.type, upsert: false });
  if (uploadError) throw uploadError;

  const { data } = client.storage.from("avatars").getPublicUrl(path);
  return { publicUrl: data.publicUrl, path };
}
